import React, { useMemo } from "react";
import { getVal, setVal } from "oolib";

import { useBarChartConfig } from "./ConfigForm_Context";
import { ConfigForm_SectionWrapper } from "../../WidgetMetricCard/ConfigForm/ConfigForm_SectionWrapper";
import { DataFieldInputSelector } from "@/components/DataFieldInputSelector";
import { CollapseTransition } from "../../utils/CollapseTransition";
import { CombineSegmentsControl, SwitchRow } from "../../utils/CombineSegmentsControl";
import { useCombineSegments } from "../../utils/useCombineSegments";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { DEFAULT_MAX_BARS, MIN_BARS } from "../constants";

// Only meaningful with a breakdown: without one there is a single series to
// lay out. Values match BarChart's breakdownDisplayType prop.
const VIEW_AS_OPTIONS = [
  { display: "Group", value: "grouped" },
  { display: "Stack", value: "stacked" },
];

const DISPLAY_MODE_OPTIONS = [
  { display: "Count", value: "countOnly" },
  { display: "Percentage", value: "percentageOnly" },
  { display: "Count, Percentage", value: "countAndPercentage" }
];

// Values match SortOption in dataviz/utils/usePrepareData.
const SORT_OPTIONS = [
  { display: "A-Z", value: "az" },
  { display: "Z-A", value: "za" },
  { display: "Highest value first", value: "desc" },
  { display: "Lowest value first", value: "asc" },
];

const sortValue = (value, dataType) => ({
  ...SORT_OPTIONS.find((opt) => opt.value === (value || "desc")),
  dataType,
});

export const ConfigForm_DisplaySection = () => {
  const {
    widgetConfig,
    updateWidget,
    dataKeys,
  } = useBarChartConfig();
  const { widgetDataStore } = useReportLandingContext();

  const currentMode = getVal(widgetConfig, dataKeys.displayMode) || 'countAndPercentage';

  const viewAsValue = useMemo(() => {
    const value = getVal(widgetConfig, dataKeys.breakdownDisplayType) || "grouped";
    return {
      ...VIEW_AS_OPTIONS.find(opt => opt.value === value),
      dataType: "View as",
    };
  }, [widgetConfig, dataKeys.breakdownDisplayType]);

  const displayValue = useMemo(() => {
    const match = DISPLAY_MODE_OPTIONS.find(opt => opt.value === currentMode);
    if (!match) return null;
    return { ...match, dataType: "Stats to Show" };
  }, [currentMode]);

  // Combining only applies once a data field is added, and only without a
  // breakdown — a breakdown chart renders grouped bars, which this can't collapse.
  const hasDim1 = !!getVal(widgetConfig, dataKeys.dimension1);
  const isBreakdown = hasDim1 && !!getVal(widgetConfig, dataKeys.dimension2);
  const combineIsVisible = hasDim1 && !isBreakdown;

  const groupsFollowFirst = getVal(widgetConfig, dataKeys.groupsFollowFirst) ?? true;
  // Alphabetical bar order is the same in every group, so "follow the first
  // group" only means something when bars are sorted by value.
  const barsSortedByValue = !["az", "za"].includes(getVal(widgetConfig, dataKeys.sortBars));

  const combineBars = useCombineSegments({
    widgetConfig,
    dataKeys,
    updateWidget,
    data: widgetDataStore?.[widgetConfig.id],
    isEnabled: combineIsVisible,
    defaultMax: DEFAULT_MAX_BARS,
    min: MIN_BARS,
  });

  return (
    <ConfigForm_SectionWrapper icon="Eye" title="Display">
      <CollapseTransition isVisible={isBreakdown}>
        <DataFieldInputSelector
          id={dataKeys.breakdownDisplayType}
          options={VIEW_AS_OPTIONS}
          value={viewAsValue}
          onChange={(id, v) => {
            updateWidget(w => setVal(w, dataKeys.breakdownDisplayType, v.value));
          }}
          placeholder="Select view"
          placeholderIcon="ChartBar"
          trailingIcon="CaretRight"
          tooltip={{ text: "Choose how breakdown bars are laid out", placement: "top" }}
          hideSearchHeader
        />
      </CollapseTransition>

      <DataFieldInputSelector
        id={dataKeys.displayMode}
        options={DISPLAY_MODE_OPTIONS}
        value={displayValue}
        onChange={(id, v) => {
          updateWidget(w => setVal(w, dataKeys.displayMode, v.value));
        }}
        placeholder="Select display mode"
        placeholderIcon="Eye"
        trailingIcon="CaretRight"
        tooltip={{ text: "Choose what stats to display", placement: "top" }}
        hideSearchHeader
      />

      <CollapseTransition isVisible={hasDim1}>
        <DataFieldInputSelector
          id={dataKeys.sortBars}
          options={SORT_OPTIONS}
          value={sortValue(getVal(widgetConfig, dataKeys.sortBars), "Sort bars")}
          onChange={(id, v) => updateWidget(w => setVal(w, dataKeys.sortBars, v.value))}
          placeholderIcon="SortAscending"
          trailingIcon="CaretRight"
          hideSearchHeader
        />
      </CollapseTransition>

      <CollapseTransition isVisible={isBreakdown && barsSortedByValue}>
        <SwitchRow
          id="groupsFollowFirst"
          label="All groups follow the first group's sorting"
          value={groupsFollowFirst}
          onChange={(next) => updateWidget(w => setVal(w, dataKeys.groupsFollowFirst, next))}
        />
      </CollapseTransition>

      <CollapseTransition isVisible={isBreakdown}>
        <DataFieldInputSelector
          id={dataKeys.sortGroups}
          options={SORT_OPTIONS}
          value={sortValue(getVal(widgetConfig, dataKeys.sortGroups), "Sort groups")}
          onChange={(id, v) => updateWidget(w => setVal(w, dataKeys.sortGroups, v.value))}
          placeholderIcon="SortAscending"
          trailingIcon="CaretRight"
          hideSearchHeader
        />
      </CollapseTransition>

      <CollapseTransition isVisible={combineIsVisible}>
        <CombineSegmentsControl
          label="Combine bars"
          countLabel="Number of bars"
          {...combineBars}
        />
      </CollapseTransition>

      <SwitchRow
        id="hideZeroValues"
        label="Hide Zero Values"
        value={!!getVal(widgetConfig, dataKeys.hideZeroValues)}
        onChange={(next) => updateWidget(w => setVal(w, dataKeys.hideZeroValues, next))}
      />
    </ConfigForm_SectionWrapper>
  );
};
